import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {filter, interval, Observable, Subject, switchMap, take, throwError} from 'rxjs';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {catchError, map} from 'rxjs/operators';
import {Settings} from '../../settings/settings';
import {LocalStorage} from '../../utils/storage';
import {Routenames} from '../../routenames';
import {ConfirmDialogService} from '../confirm-dialog-service/confirm-dialog.service';
import {MatDialog} from '@angular/material/dialog';
import {Utils} from '../../utils.class';

export interface ApiResponse<T> {
    success?: boolean;
    message?: string;
    data?: T;
    length?: number;
}

export interface ErrorData {
    error: string;
    errormessage: string;
    data: any;
}

export class ApiErrorResponse implements ApiResponse<ErrorData> {
    data?: ErrorData;
    message?: string;
    success?: boolean;
    status?: number;
}

export enum SaveState {
    changes, save, saving, saved, error, stored, deleting, deleted
}

@Injectable()
export class ApiService {

    response: Subject<Object> = new Subject();
    response$: Observable<Object>;

    static loadingCount = 0;
    static totalLoaded = 0;

    constructor(private http: HttpClient,
                private router: Router,
                private dialog: MatDialog,
                private confirmDialogService: ConfirmDialogService) {
        this.response$ = this.response.asObservable();
    }

    clearCache() {
        return this.http.get('/clear-cache').subscribe();
    }

    isTokenExpired() {
        if (LocalStorage.userToken) {
            const tokenExpiry = Utils.getJwtData(LocalStorage.userToken).exp * 1000;
            return tokenExpiry < (new Date()).getTime();
        }
        return false;
    }

    getCall$<T>(apiUrl: string, params = {}, noErrorDialogs?: boolean, skipWaitForExpiredToken = false): Observable<ApiResponse<T>> {
        params = this.buildURLParams(params);
        return interval(this.isTokenExpired() && !skipWaitForExpiredToken ? 200 : 0).pipe(
            filter(() => {
                return !this.isTokenExpired() || skipWaitForExpiredToken;
            }),
            take(1),
            switchMap(() => this.http.get(
                `${Settings.API_ENDPOINT}${apiUrl}`, {params})
                .pipe(map(response => {
                    this.response.next(response);
                    return response as ApiResponse<T>;
                }), catchError(error => {
                    return throwError(this.onError(error, noErrorDialogs));
                }))
            )
        );
    }

    postCall$<T>(apiUrl: string, params = {}, noErrorDialogs?: boolean): Observable<ApiResponse<T>> {
        return this.http.post(`${Settings.API_ENDPOINT}${apiUrl}`, Utils.removeFunctions(params))
            .pipe(map(response => {
                this.response.next(response);
                return response as ApiResponse<T>;
            }), catchError(error => {
                return throwError(this.onError(error, noErrorDialogs));
            }));
    }

    patchCall$<T>(apiUrl: string, params = {}): Observable<ApiResponse<T>> {
        return this.http.patch(`${Settings.API_ENDPOINT}${apiUrl}`, params)
            .pipe(map(response => {
                this.response.next(response);
                return response as ApiResponse<T>;
            }), catchError(error => {
                return throwError(this.onError(error));
            }));
    }

    putCall$<T>(apiUrl: string, params = {}): Observable<ApiResponse<T>> {

        return this.http.put(`${Settings.API_ENDPOINT}${apiUrl}`, params)
            .pipe(map(response => {
                this.response.next(response);
                return response as ApiResponse<T>;
            }), catchError(error => {
                return throwError(this.onError(error));
            }));
    }

    deleteCall$<T>(apiUrl: string): Observable<ApiResponse<T>> {
        return this.http.delete(`${Settings.API_ENDPOINT}${apiUrl}`)
            .pipe(map(response => {
                this.response.next(response);
                return response as ApiResponse<T>;
            }), catchError(error => {
                return throwError(this.onError(error));
            }));
    }

    // get call with blob as return format
    getBlobCall(apiUrl: string, params = {}): Observable<any> {
        params = this.buildURLParams(params);
        let headers = new HttpHeaders();
        headers = headers.append('Authorization', 'Bearer ' + LocalStorage.userToken);
        headers = headers.append('Accept', '*');

        return this.http.get(`${Settings.API_ENDPOINT}${apiUrl}`, {params, responseType: 'blob', headers});
    }

    /**
     * Make a request for one file
     * @param apiUrl
     * @param file
     * @param params
     */
    makeFileRequest$<T>(apiUrl: string, file: File, params = {}): Observable<ApiResponse<T>> {
        return new Observable(observer => {
            const formData: FormData = new FormData(),
                xhr: XMLHttpRequest = new XMLHttpRequest();

            formData.append('upload', file, file.name);
            for (const key in params) {
                if (params.hasOwnProperty(key)) {
                    const value = typeof params[key] === 'object' ? JSON.stringify(params[key]) : params[key];
                    formData.append(key, value);
                }
            }

            xhr.onreadystatechange = () => {
                if (xhr.readyState === 4) {
                    if (xhr.status === 200) {
                        observer.next(JSON.parse(xhr.response));
                        observer.complete();
                    } else {
                        observer.error(this.onError({status: xhr.status, error: JSON.parse(xhr.response)}));
                        observer.complete();
                    }
                }
            };

            xhr.open('POST', `${Settings.API_ENDPOINT}${apiUrl}`, true);
            xhr.setRequestHeader('Accept', 'application/json');
            xhr.setRequestHeader('Authorization', 'Bearer ' + LocalStorage.userToken);
            xhr.send(formData);
        });
    }

    private redirectIf401(errorStatus: number) {
        if (errorStatus === 401) {
            this.router.navigate([Routenames.Logout]);
            return true;
        }
        return false;
    }

    private buildURLParams(data: Object): HttpParams {
        let params = new HttpParams();
        for (const key in data) {
            if (data.hasOwnProperty(key)) {
                const value = data[key];
                if (Array.isArray(value)) {
                    for (const item of value) {
                        params = params.append(`${key}[]`, item);
                    }
                } else {
                    params = params.set(key, value);
                }
            }
        }
        return params;
    }

    private onError(error: any, noErrorDialogs?: boolean): ApiErrorResponse {
        const err = new ApiErrorResponse();
        if (typeof error['error'] !== 'undefined') {
            if (typeof error['error']['data'] !== 'undefined') {
                err.data = error['error']['data'];
            }
            if (typeof error['error']['message'] !== 'undefined') {
                err.message = error['error']['message'];
            }
        } else {
            if (typeof error === 'string') {
                try {
                    error = JSON.parse(error);
                } catch (e) {

                }
                err.data = error;
            } else {
                err.data = new class implements ErrorData {
                    data = error;
                    error: string;
                    errormessage: string;
                };
            }
            err.message = 'Some server error';

        }
        err.success = false;
        err.status = error.status;
        console.log(err, error, noErrorDialogs);

        if (!noErrorDialogs) {
            if (error.status === 401) { // Unauthorized
                this.dialog.closeAll();
                this.router.navigateByUrl(Routenames.Logout);
            } else if (error.status === 403) {
                this.dialog.closeAll();
                this.confirmDialogService.confirmClose(
                    'Je hebt onvoldoende rechten',
                    error.error?.message
                );
            } else if (error.status === 404) {
                this.dialog.closeAll();
                this.confirmDialogService.confirmClose(
                    'Niet gevonden',
                    error.error?.message
                );
            } else if (error.status === 406 || error.status === 402) {
                this.dialog.closeAll();
                this.confirmDialogService.confirmClose(
                    'Fout tijdens opslaan',
                    err.message ?? error.message
                );
            } else if (error.status === 409) {
                this.dialog.closeAll();
                this.confirmDialogService.confirmClose(
                    'Conflict tijdens opslaan',
                    'Dit item is vanaf een ander scherm aangepast sinds jij het hebt geopend. De wijzigingen zijn niet opgeslagen.'
                );
            }
        }

        return err;
    }

}
